/**
 * 名前だけの一覧（業務・アクター・ドメイン）の本体。
 *
 * 3 型とも一覧で読みたいものが名前しか無い。業務は説明も帰属も持つが、それは 1 件を開いて
 * 読むものであって、128 件が並ぶ場所で見比べる値ではない。行に添える値が無いので、型ごとに
 * 行の組み立てを書き分ける理由も無い。3 型で 1 実装にする。
 *
 * **ID は行に出さない。** 読み手が見分けるのは名前であり、ID は詳細を開けば読める。ID を
 * 添えると 1 行が 2 段になり、名前だけを縦に走査する読み方ができなくなる。名前が取れない行も
 * ID には倒さず「未設定」と出す（`displayLabel`）。人は ID で行を判別できない。
 *
 * 件数は見出しに添える。ここでの件数は画面に並んでいる行そのものの数なので、読み手が数えたい
 * 数と一致する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務・アクター・ドメインの一覧を、名前だけの行と詳細への導線で描く。
 * @graph-connects SpecNodeLink [calls] 各詳細への遷移
 */
import type { NamedNodeListItem } from '@cortex/domain-spec-console';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState, ErrorState } from '../components/spec/fallback-states.js';
import { SpecNodeLink } from '../components/spec/SpecNodeLink.js';
import { SpecLabelBadges, displayLabel } from '../components/spec/UntranslatedBadge.js';
import type { SpecNodeTypeValue } from '../lib/spec-node-types.js';

/** ローディング時に出すスケルトンの行数。 @graph-connects none */
const SKELETON_ROWS = 8;

/** `NamedNodeListPage` の props。 */
export interface NamedNodeListPageProps {
  /** 一覧するノード型。行の遷移先はこれで決まる。 */
  nodeType: SpecNodeTypeValue;
  /** 見出しの翻訳キー。 */
  titleKey: string;
  /** 0 件のときの翻訳キー。 */
  emptyKey: string;
  /**
   * 絞り込み欄の翻訳キー。置かないなら省略する。
   *
   * 分類マスター（アクター・ドメイン）は 1 桁の件数で運用する集合なので、絞り込み欄を置くと
   * 全部が見えている一覧の上に使い道の無い入力が乗る。
   */
  filterKeys?: { label: string; placeholder: string };
  /** 一覧の中身。 */
  items: readonly NamedNodeListItem[];
  /** 取得中か。 */
  isLoading: boolean;
  /**
   * 取得エラー。
   *
   * PJ 番号はここで受け取らない。行のリンクは `SpecNodeLink` が現在地から読むので、渡す形に
   * すると使われない prop が残り、渡し忘れが型でも lint でも落ちない状態を作る。
   */
  error: unknown;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前だけの一覧を、見出し・件数・絞り込み・行の 4 要素で描く。
 * @graph-connects SpecNodeLink [calls] 各詳細への遷移
 */
export function NamedNodeListPage({
  nodeType,
  titleKey,
  emptyKey,
  filterKeys,
  items,
  isLoading,
  error,
}: NamedNodeListPageProps) {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const filtered = items.filter((item) => matchesNamedNode(item, keyword.trim()));

  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t(titleKey)}</h1>
        {!isLoading && (
          <span className="text-sm text-[var(--color-text-muted)]">
            {t('spec.countItems', { count: filtered.length })}
          </span>
        )}
      </header>

      {filterKeys !== undefined && (
        <Input
          fullWidth
          label={t(filterKeys.label)}
          placeholder={t(filterKeys.placeholder)}
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
          }}
        />
      )}

      {isLoading ? (
        <div className="flex flex-col gap-1" aria-busy="true" aria-label={t('common.loading')}>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} variant="rectangular" height="2rem" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState message={t(emptyKey)} />
      ) : (
        <ul className="flex flex-col">
          {filtered.map((item) => (
            <li
              key={item.nodeId}
              className="flex items-center gap-2 border-b border-[var(--color-border)] py-2 last:border-b-0"
            >
              <SpecNodeLink
                nodeType={nodeType}
                nodeId={item.nodeId}
                className="min-w-0 truncate text-sm text-[var(--color-accent-primary)] underline"
              >
                {displayLabel(item.label, t, item.untranslated)}
              </SpecNodeLink>
              <SpecLabelBadges label={item.label} untranslated={item.untranslated} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 1 行が絞り込みに一致するか。名前と ID のどちらでも拾う。
 *
 * ID を行に出さないのに ID でも拾うのは、実装から来た人が ID で引くためである。出す値と
 * 引ける値は別の関心で、引ける範囲を狭めても画面は静かにならない。
 *
 * @param item 一覧の 1 行
 * @param keyword 絞り込み語（前後の空白は呼び出し側で落とす）
 * @returns 一致するなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前だけの一覧の 1 行が絞り込み条件に一致するかを判定する。
 * @graph-connects none
 */
export function matchesNamedNode(item: NamedNodeListItem, keyword: string): boolean {
  if (keyword === '') return true;
  const needle = keyword.toLowerCase();
  return item.label.toLowerCase().includes(needle) || item.nodeId.toLowerCase().includes(needle);
}
